import { memo, ReactNode, createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface AnimatedToastContextValue {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
}

const AnimatedToastContext = createContext<AnimatedToastContextValue | null>(null);

export const useAnimatedToast = () => {
  const context = useContext(AnimatedToastContext);
  if (!context) {
    throw new Error('useAnimatedToast must be used within AnimatedToastProvider');
  }
  return context;
};

interface AnimatedToastProviderProps {
  children: ReactNode;
}

/**
 * Animated Toast Provider
 * Provides a premium toast notification system with smooth animations.
 * Features slide-in/out effects and auto-dismiss with progress indicator.
 */
export const AnimatedToastProvider = memo(({ children }: AnimatedToastProviderProps) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: ToastType = 'info', duration = 4000) => {
    const id = `toast-${Date.now()}`;
    const newToast: Toast = { id, message, type, duration };
    
    setToasts(prev => [...prev, newToast]);
    
    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'info':
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getBorderColor = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'border-l-green-500';
      case 'error':
        return 'border-l-red-500';
      case 'info':
      default:
        return 'border-l-blue-500';
    }
  };

  return (
    <AnimatedToastContext.Provider value={{ showToast }}>
      {children}
      
      {/* Toast container */}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm">
        <AnimatePresence mode="popLayout">
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              layout
              initial={{ opacity: 0, x: 100, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 100, scale: 0.9 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              className={cn(
                'relative bg-card/95 backdrop-blur-lg rounded-lg shadow-xl border border-border/50',
                'p-4 pr-10 border-l-4',
                getBorderColor(toast.type)
              )}
            >
              <div className="flex items-start gap-3">
                {getIcon(toast.type)}
                <p className="text-sm text-foreground">{toast.message}</p>
              </div>
              
              <button
                onClick={() => removeToast(toast.id)}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
              
              {/* Progress bar */}
              {toast.duration && toast.duration > 0 && (
                <motion.div
                  className="absolute bottom-0 left-0 h-1 bg-primary/30 rounded-b-lg"
                  initial={{ width: '100%' }}
                  animate={{ width: '0%' }}
                  transition={{ duration: toast.duration / 1000, ease: 'linear' }}
                />
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </AnimatedToastContext.Provider>
  );
});

AnimatedToastProvider.displayName = 'AnimatedToastProvider';
